import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import Footer from "../components/Footer";
import Icon from "../components/Icon";
import { setPageMeta } from "../utils/meta";

/**
 * The landing page is now a single hero: a slideshow of lighting conditions,
 * each drawn in the light it's shot in. Everything else about the catalogue
 * lives one tap away in the gallery and the builder — this page's only job
 * is to say what the site is and point at those two doors.
 *
 * The images are illustrative photography (Unsplash), deliberately kept
 * behind a tint so they read as mood, not as claims about a specific
 * camera. The numbers still belong to the recipe pages; nothing here
 * pretends to be a settings answer.
 */
const HERO_SLIDES = [
  {
    lightingId: "golden-hour",
    eyebrow: "Golden hour",
    title: "The light is only right for twenty minutes.",
    body: "Get your shutter, ISO and white balance locked before it drops, so the whole window goes to shooting instead of fighting menus.",
    image: "/images/hero/golden-hour.jpg",
    alt: "Warm golden-hour light over a landscape",
  },
  {
    lightingId: "full-sun",
    eyebrow: "Full sun",
    title: "Midday sun is where an ND filter earns its place.",
    body: "At a 180° shutter you're several stops over. Each recipe tells you the exact filter that closes the gap on your camera.",
    image: "/images/hero/full-sun.jpg",
    alt: "Bright midday sun",
  },
  {
    lightingId: "blue-hour",
    eyebrow: "Blue hour",
    title: "When ISO does the work, not shutter.",
    body: "There's very little light left, so the work shifts to ISO. The recipe tells you where your camera's usable ceiling actually sits.",
    image: "/images/hero/blue-hour.jpg",
    alt: "Cool blue-hour twilight",
  },
  {
    lightingId: "overcast",
    eyebrow: "Overcast",
    title: "Flat light, clean answer.",
    body: "No filter, base ISO, a slightly cooler white balance. The plain conditions are the ones a table nails every time.",
    image: "/images/hero/overcast.jpg",
    alt: "Flat light under an overcast sky",
  },
  {
    lightingId: "low-light",
    eyebrow: "Low light",
    title: "Know the ceiling before you hit it.",
    body: "Every recipe states the usable ISO ceiling for your camera, so you plan the shot around the light you actually have.",
    image: "/images/hero/low-light.jpg",
    alt: "A dim, low-light scene",
  },
];

const SLIDE_MS = 6000;

export default function Home() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reduced, setReduced] = useState(false);

  const touchStartX = useRef(null);

  useEffect(() => {
    setPageMeta({
      title: "Filtspot: Camera Settings by Scenario",
      description:
        "Exact camera settings for any shoot: shutter, ISO, ND filter, white balance and profile, calculated from confirmed specs rather than guessed.",
      path: "/",
    });
  }, []);

  // Respect reduced motion: no auto-advance, no Ken Burns, no progress bar.
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (paused || reduced) return undefined;
    const t = setInterval(() => {
      setCurrent((i) => (i + 1) % HERO_SLIDES.length);
    }, SLIDE_MS);
    return () => clearInterval(t);
  }, [paused, reduced]);

  const go = (i) => setCurrent((i + HERO_SLIDES.length) % HERO_SLIDES.length);

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      go(current + 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      go(current - 1);
    } else if (e.key === " ") {
      e.preventDefault();
      setPaused((p) => !p);
    }
  };

  const onTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };
  const onTouchEnd = (e) => {
    const start = touchStartX.current;
    if (start == null) return;
    const delta = start - e.changedTouches[0].screenX;
    if (Math.abs(delta) < 50) return;
    go(current + (delta > 0 ? 1 : -1));
    setPaused(true);
  };

  const slide = HERO_SLIDES[current];

  return (
    <main className="home">
      <section
        className="hero"
        aria-roledescription="carousel"
        aria-label="What the site does, by lighting condition"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="hero-copy">
          <div key={current} className={reduced ? "" : "hero-copy-anim"}>
            <p className="eyebrow">{slide.eyebrow}</p>
            <h1>{slide.title}</h1>
            <p className="hero-lede">{slide.body}</p>
          </div>

          <p className="hero-standing">
            Every setting is computed from the camera's confirmed specs, the shot's
            frame-rate and shutter maths, and the exposure profile of the light you're in.
            Same inputs, same answer, every time, and each page shows its working.
          </p>

          <div className="hero-actions">
            <Link className="hero-action hero-action-primary" to="/gallery">
              Browse all settings
            </Link>
            <Link className="hero-action" to="/builder">
              Build one for any camera
            </Link>
          </div>
        </div>

        <div className="hero-stage" aria-live="polite">
          <img
            key={slide.image}
            className={reduced ? "hero-img" : "hero-img hero-img-anim"}
            src={slide.image}
            alt={slide.alt}
            loading="eager"
            onError={(e)=>{e.currentTarget.style.display="none";}}
          />

          <div
            key={`card-${current}`}
            className={`hero-card grad-${slide.lightingId} ${reduced ? "" : "hero-card-anim"}`}
          >
            <span className="hero-card-label">
              <Icon name="light" size={13} /> {slide.eyebrow}
            </span>
            <p className="hero-card-title">{slide.title}</p>
            <div className="hero-card-foot">
              <span className="hero-card-note">Calculated, not guessed</span>
              <Link to="/gallery" className="hero-card-link">
                Explore
              </Link>
            </div>
          </div>

          <p className="sr-only" aria-live="polite">
            Slide {current + 1} of {HERO_SLIDES.length}: {slide.eyebrow}.
          </p>

          <div className="hero-controls" aria-label="Slideshow controls">
            <div className="hero-dots" role="tablist" aria-label="Choose a condition">
              {HERO_SLIDES.map((s, i) => (
                <button
                  key={s.lightingId}
                  type="button"
                  className={`hero-dot ${i === current ? "is-active" : ""}`}
                  onClick={() => go(i)}
                  role="tab"
                  aria-selected={i === current}
                  aria-label={s.eyebrow}
                />
              ))}
            </div>
            <button
              type="button"
              className="hero-pause"
              onClick={() => setPaused((p) => !p)}
              aria-pressed={paused}
              aria-label={paused ? "Play slideshow" : "Pause slideshow"}
            >
              {paused ? (
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" width="14" height="14">
                  <path d="m8 5 11 7-11 7V5z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" width="14" height="14">
                  <path d="M7 5h3v14H7zM14 5h3v14h-3z" />
                </svg>
              )}
            </button>
          </div>

          {!reduced && (
            <div className="hero-progress" aria-hidden="true">
              <div
                key={`bar-${current}-${paused}`}
                className={`hero-progress-bar ${paused ? "is-paused" : ""}`}
                style={{ animationDuration: `${SLIDE_MS}ms` }}
              />
            </div>
          )}
        </div>
      </section>

      {/* Marker read by scripts/prerender.js */}
      <div data-prerender-ready="true" style={{ display: "none" }} />

      <Footer />
    </main>
  );
}
